import { inject, Injectable } from '@angular/core';
import { AssetDto, AssetVersionDto } from '@nusign/picflow-backend-types';
import { filter, map, startWith } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root',
})
export class AssetVersionService {
  private socketService = inject(SocketService);
  private loader = inject(ObservableDataLoaderService);

  public readonly tempOnAssetUpdated$: Observable<{
    asset: AssetDto;
    name: 'updated';
  }> = this.socketService.onSocketEvent.pipe(
    filter(e => e?.name === 'asset.updated'),
    map(event => {
      const asset = event.payload.asset as AssetDto;
      return {
        asset,
        name: 'updated' as const,
      };
    }),
    startWith({ asset: undefined as AssetDto, name: 'updated' as const })
  );
  public readonly onAssetVersionUpdated$: Observable<{
    assetVersion: AssetVersionDto;
    name: 'updated';
  }> = this.socketService.onSocketEvent.pipe(
    filter(e => e?.name === 'asset_version.updated'),
    map(event => {
      const assetVersion = event.payload.asset_version as AssetVersionDto;
      return {
        assetVersion,
        name: 'updated' as const,
      };
    }),
    startWith({ assetVersion: undefined as AssetVersionDto, name: 'updated' as const })
  );

  private assetVersionUpdateWatcher = this.onAssetVersionUpdated$.subscribe(event => {
    if (!event?.assetVersion) return;
    this.loader.updateFuzzy<AssetDto[]>(`assets_gal_`, assets => this.updateAssetVersionsCacheFn(assets, event));
    this.loader.updateFuzzy<AssetDto>(`assets/${event.assetVersion.asset}`, asset =>
      this.updateAssetVersionCacheFn(asset, event)
    );
  });

  private updateAssetVersionCacheFn(
    asset: AssetDto,
    event: {
      name: 'updated' | 'created' | 'deleted';
      assetVersion?: AssetVersionDto;
    }
  ): AssetDto {
    if (!asset) return;

    if (event.name === 'updated') {
      return {
        ...asset,
        version_data: <AssetVersionDto>{
          ...asset.version_data,
          ...event.assetVersion,
        },
      };
    }

    return asset;
  }

  private updateAssetVersionsCacheFn(
    assets: AssetDto[],
    event: { name: 'updated' | 'created' | 'deleted'; assetVersion?: AssetVersionDto }
  ) {
    if (!assets) return;
    if (event.name === 'updated') {
      // Could be because an asset moved from one section to another
      const index = assets.findIndex(a => a.id === event.assetVersion.asset);
      if (index !== -1) {
        assets[index] = {
          ...assets[index],
          version_data: <AssetVersionDto>{
            ...assets[index].version_data,
            ...event.assetVersion,
          },
        };
        return assets;
      }
    }
    return assets;
  }
}
